import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { HugeiconsIcon } from '@hugeicons/react'
import { Calendar01Icon, Clock01Icon, Tick02Icon } from '@hugeicons/core-free-icons'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { cn, now } from '@/lib/utils'

function toDateInput(timestamp: number): string {
  return dayjs(timestamp * 1000).format('YYYY-MM-DD')
}

function toTimeInput(timestamp: number): string {
  return dayjs(timestamp * 1000).format('HH:mm')
}

function roundToNextHour(): number {
  const d = dayjs().add(1, 'hour')
  return d.minute(0).second(0).millisecond(0).unix()
}

export default function SchedulePicker({
  scheduledAt,
  onChange
}: {
  scheduledAt: number | null
  onChange: (ts: number | null) => void
}) {
  const { t } = useTranslation()
  const initial = useMemo(
    () => (scheduledAt != null ? scheduledAt : roundToNextHour()),
    [scheduledAt]
  )
  const [date, setDate] = useState(toDateInput(initial))
  const [time, setTime] = useState(toTimeInput(initial))

  useEffect(() => {
    if (scheduledAt == null) return
    setDate(toDateInput(scheduledAt))
    setTime(toTimeInput(scheduledAt))
  }, [scheduledAt])

  const parsed = useMemo(() => {
    if (!date || !time) return null
    const d = dayjs(`${date}T${time}`, 'YYYY-MM-DDTHH:mm')
    if (!d.isValid()) return null
    return d.unix()
  }, [date, time])

  const inFuture = parsed != null && parsed > now()
  const isDirty = parsed != null && parsed !== scheduledAt
  const isActive = scheduledAt != null

  return (
    <div className="space-y-3 w-72">
      <div className="flex items-center gap-2 text-sm font-medium">
        <HugeiconsIcon icon={Calendar01Icon} size={18} />
        {t('Schedule post')}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={Calendar01Icon} className="text-muted-foreground" size={16} />
          <Input
            type="date"
            value={date}
            min={toDateInput(now())}
            onChange={(e) => setDate(e.target.value)}
            className="flex-1"
          />
        </div>
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={Clock01Icon} className="text-muted-foreground" size={16} />
          <Input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="flex-1"
          />
        </div>
      </div>
      {parsed != null && !inFuture && (
        <div className="text-xs text-destructive">{t('Pick a future date and time')}</div>
      )}
      <div className="flex gap-2">
        <Button
          variant="default"
          className="flex-1"
          disabled={!inFuture || !isDirty}
          onClick={() => {
            if (parsed != null && inFuture) onChange(parsed)
          }}
        >
          <HugeiconsIcon icon={Tick02Icon} size={16} />
          {t('Set')}
        </Button>
        {isActive && (
          <Button
            variant="outline"
            onClick={() => {
              onChange(null)
              const next = roundToNextHour()
              setDate(toDateInput(next))
              setTime(toTimeInput(next))
            }}
          >
            {t('Clear')}
          </Button>
        )}
      </div>
    </div>
  )
}

export const ScheduleButton = forwardRef<
  HTMLButtonElement,
  {
    scheduledAt: number | null
    isActive: boolean
    disabled?: boolean
    onClick?: () => void
  }
>(function ScheduleButton({ scheduledAt, isActive, disabled, onClick }, ref) {
  const { t } = useTranslation()
  const label = useMemo(() => {
    if (scheduledAt == null) return t('Schedule')
    return dayjs(scheduledAt * 1000).format('MMM D, HH:mm')
  }, [scheduledAt, t])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      title={label}
      disabled={disabled}
      className={cn(isActive && 'bg-primary/15 text-primary hover:bg-primary/15')}
      onClick={onClick}
    >
      <HugeiconsIcon icon={Calendar01Icon} />
    </Button>
  )
})
